/**
 * Security utilities for client-side encryption and security measures
 * 
 * IMPORTANT NOTE:
 * - The primary security comes from HTTPS/TLS encryption during transmission
 * - These utilities provide additional obfuscation but are NOT a replacement for HTTPS
 * - Passwords are hashed on the server using BCrypt before storage
 */

import CryptoJS from 'crypto-js';

class SecurityUtils {
  /**
   * Generate a random encryption key for this session
   * This key is used for client-side encryption before sending to server
   */
  generateSessionKey() {
    return CryptoJS.lib.WordArray.random(256/8).toString();
  }

  /**
   * Encrypt data using AES encryption
   * @param {string} data - Data to encrypt
   * @param {string} key - Encryption key
   * @returns {string} Encrypted data
   */
  encrypt(data, key) {
    return CryptoJS.AES.encrypt(data, key).toString();
  }

  /**
   * Decrypt data using AES encryption
   * @param {string} encryptedData - Encrypted data
   * @param {string} key - Encryption key
   * @returns {string} Decrypted data
   */
  decrypt(encryptedData, key) {
    const bytes = CryptoJS.AES.decrypt(encryptedData, key);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  /**
   * Hash data using SHA256
   * Note: This is for client-side hashing only. Server still does BCrypt hashing.
   * @param {string} data - Data to hash
   * @returns {string} Hashed data
   */
  hash(data) {
    return CryptoJS.SHA256(data).toString();
  }

  /**
   * Encode credentials in Base64 (basic obfuscation)
   * @param {string} username 
   * @param {string} password 
   * @returns {object} Encoded credentials
   */
  encodeCredentials(username, password) {
    return {
      u: CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(username)),
      p: CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(password))
    };
  }

  /**
   * Decode credentials from Base64
   * @param {string} encodedUsername 
   * @param {string} encodedPassword 
   * @returns {object} Decoded credentials
   */
  decodeCredentials(encodedUsername, encodedPassword) {
    return {
      username: CryptoJS.enc.Base64.parse(encodedUsername).toString(CryptoJS.enc.Utf8),
      password: CryptoJS.enc.Base64.parse(encodedPassword).toString(CryptoJS.enc.Utf8)
    };
  }

  /**
   * Clear sensitive data from memory
   * @param {object} obj - Object containing sensitive data
   */
  clearSensitiveData(obj) {
    if (obj && typeof obj === 'object') {
      Object.keys(obj).forEach(key => {
        if (obj[key] && typeof obj[key] === 'string') {
          obj[key] = '';
        }
      });
    }
  }

  /**
   * Validate password strength
   * @param {string} password 
   * @returns {object} Validation result with strength score and feedback
   */
  validatePasswordStrength(password) {
    const result = {
      isValid: false,
      score: 0,
      feedback: []
    };

    if (!password) {
      result.feedback.push('Password is required');
      return result;
    }

    // Length check
    if (password.length < 8) {
      result.feedback.push('Password must be at least 8 characters long');
    } else {
      result.score += 1;
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
      result.score += 1;
    } else {
      result.feedback.push('Include at least one uppercase letter');
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
      result.score += 1;
    } else {
      result.feedback.push('Include at least one lowercase letter');
    }

    // Number check
    if (/[0-9]/.test(password)) {
      result.score += 1;
    } else {
      result.feedback.push('Include at least one number');
    }

    // Special character check
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      result.score += 1;
    } else {
      result.feedback.push('Include at least one special character');
    }

    result.isValid = result.score >= 3 && password.length >= 8;
    
    return result;
  }

  /**
   * Generate a secure random password
   * @param {number} length - Password length (default: 16)
   * @returns {string} Generated password
   */
  generateSecurePassword(length = 16) {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    let password = '';
    const randomValues = new Uint32Array(length);
    window.crypto.getRandomValues(randomValues);
    
    for (let i = 0; i < length; i++) {
      password += charset[randomValues[i] % charset.length];
    }
    
    return password;
  }
}

export default new SecurityUtils();
